import classNames from 'classnames'
import {memo, useCallback} from 'react'
import {useLocation} from 'react-router-dom'

import {getHrefWithQueryParams} from '../../../../../../routes'
import RouterLink from '../../../../../common/RouterLink/RouterLink'
import {TestScopesKey} from '../../../../../packages/Tests/Tests.types'
import {getTestScope, trimAndRemoveColon} from '../../../../../packages/Tests/Tests.utils'
import {getFilterQueryParams} from '../../BuildTestsTab.utils'

import styles from './TestScopeBreadcrumbs.css'

type Props = {
  className?: string
}

function TestScopeBreadcrumbs({className}: Props) {
  const location = useLocation()
  const {scope} = getFilterQueryParams(location)
  const getScopeName = useCallback(
    (scopeKey: TestScopesKey) => {
      const scopeValue = scope[scopeKey]
      return scopeValue ? trimAndRemoveColon(scopeValue) : `<no ${scopeKey}>`
    },
    [scope],
  )
  const renderBaseLink = useCallback(
    (scopeKey?: TestScopesKey) => (
      <RouterLink
        className={classNames(styles.link, {
          [styles.all]: scopeKey == null,
          [styles.noScope]: scopeKey != null && !scope[scopeKey],
        })}
        to={getHrefWithQueryParams(location, null, prevParams => ({
          ...prevParams,
          ...getTestScope(scopeKey, scope),
          page: null,
          expandedTest: null,
        }))}
      >
        {scopeKey != null ? getScopeName(scopeKey) : 'All'}
      </RouterLink>
    ),
    [getScopeName, location, scope],
  )
  const renderLink = useCallback(
    (scopeKey: TestScopesKey, active: boolean) =>
      active ? (
        renderBaseLink(scopeKey)
      ) : (
        <span
          className={classNames(styles.link, {
            [styles.noScope]: scopeKey != null && !scope[scopeKey],
          })}
        >
          {getScopeName(scopeKey)}
        </span>
      ),
    [getScopeName, renderBaseLink, scope],
  )
  const renderLinkWithSeparator = useCallback(
    (scopeKey: TestScopesKey, active: boolean) => (
      <>
        {' / '}
        {renderLink(scopeKey, active)}
      </>
    ),
    [renderLink],
  )
  const showSuite = scope.suite != null || scope.package != null || scope.class != null
  const showPackage = scope.package != null || scope.class != null
  const showClass = scope.class != null
  return showSuite ? (
    <div className={classNames(className, styles.wrapper)}>
      {'Current scope: '}
      {renderBaseLink()}
      {showSuite && renderLinkWithSeparator(TestScopesKey.SUITE, showPackage)}
      {showPackage && renderLinkWithSeparator(TestScopesKey.PACKAGE, showClass)}
      {showClass && renderLinkWithSeparator(TestScopesKey.CLASS, false)}
    </div>
  ) : null
}

export default memo<Props>(TestScopeBreadcrumbs)
